import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StoreFormActions from "@store/Components/Store/StoreFormActions";
import { getStoreSettingsCollection } from "@store/Store/Context/ContextSelectors";
import toggleDownloadModalOpen from "@store/Store/Pages/InvoiceHistory/Handlers/ToggleDownloadModalOpen";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { datepickerWrapperStyles } from "@store/theme/common/mixins/datepicker";
import { formNarrowGridProps } from "@store/theme/common/props/form";
import { mobileFiltersModalProps } from "@store/theme/common/props/mobileFiltersModal";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import downloadCsv from "@store/utilities/downloadCsv";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface State {
    fromDate: Date;
    toDate: Date | undefined;
}

const mapStateToProps = (state: StoreApplicationState) => ({
    settings: getStoreSettingsCollection(state),
    isDownloadModalOpen: state.store.pages.invoiceHistory.isDownloadModalOpen,
});

const mapDispatchToProps = {
    toggleDownloadModalOpen,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface InvoiceHistoryDownloadStyles {
    container?: GridContainerProps;
    heading?: TypographyProps;
    headingItem?: GridItemProps;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    buttonsItem?: GridItemProps;
    downloadButton?: ButtonProps;
    downloadModal?: ModalPresentationProps;
    downloadInvoiceNote?: TypographyPresentationProps;
    modalTitleWrapper?: InjectableCss;
    modalTitle?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    modalDownloadButton?: ButtonPresentationProps;
}

const _datePickerGridItem: GridItemProps = {
    width: [12, 6, 6, 3, 2],
    css: css`
        ${datepickerWrapperStyles()}
    `,
};

export const downloadStyles: InvoiceHistoryDownloadStyles = {
    container: formNarrowGridProps,
    headingItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        as: "h2",
        css: css`
            margin: 0;
        `,
    },
    fromGridItem: _datePickerGridItem,
    toGridItem: _datePickerGridItem,
    buttonsItem: {},
    downloadButton: {
        variant: "primary",
    },
    downloadInvoiceNote: {
        color: "common.disabled",
        weight: "bold",
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            display: flex;
            max-width: 12rem;
            margin-left: 0.5rem;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, "relative"], properties: ["position"] },
                        { values: [null, null, null, "0.25rem"], properties: ["top"] },
                        { values: [null, null, null, "100%"], properties: ["max-width"] },
                        { values: [null, null, null, "0"], properties: ["margin-left"] },
                    ],
                    mediaOptions.max,
            )}
        `
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    downloadModal: mobileFiltersModalProps.modal,
    modalTitleWrapper: mobileFiltersModalProps.modalTitleWrapper,
    modalTitle: mobileFiltersModalProps.modalTitle,
    buttonsWrapper: mobileFiltersModalProps.buttonsWrapper,
    modalDownloadButton: mobileFiltersModalProps.primaryButton,
};

const styles = downloadStyles;
class InvoiceHistoryDownload extends React.Component<Props, State> {
    invoiceDownloadLookBackDays = this.props.settings.poolcorpSettings.invoiceDownloadLookBackDays;
    invoiceDownloadMaximumInterval = this.props.settings.poolcorpSettings.invoiceDownloadMaximumInterval;
    datePickerFormat = this.props.settings.poolcorpSettings.datePickerFormat

    constructor(props: Props) {
        super(props);

        this.state = {
            fromDate: new Date(),
            toDate: undefined,
        };
    }

    fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">, update?: boolean) => {
        if (selectedDay) {
            const maxNewToDate = new Date(selectedDay.getTime());
            maxNewToDate.setDate(selectedDay.getDate() + this.invoiceDownloadMaximumInterval);

            if (this.state.toDate && this.state.toDate.getTime() > maxNewToDate.getTime()) {
                this.setState({
                    fromDate: selectedDay,
                    toDate: undefined,
                });
                return;
            }
            if (this.state.toDate && this.state.toDate?.getTime() < selectedDay.getTime()) {
                this.setState({
                    fromDate: selectedDay,
                    toDate: undefined,
                });
                return;
            }
            this.setState({
                fromDate: selectedDay,
            });
        }
    };

    toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">, update?: boolean) => {
        if (selectedDay) {
            this.setState({
                toDate: selectedDay,
            });
        }
    };

    closeModal = () => {
        if (this.props.isDownloadModalOpen) {
            this.props.toggleDownloadModalOpen();
        }
    };

    render() {
        const fromDate = this.state.fromDate;
        const toDate = this.state.toDate;

        const today = new Date();
        const fromMinDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - this.invoiceDownloadLookBackDays,
        );

        let toMaxDate;
        if (fromDate) {
            const newMaxTo = new Date(fromDate.getTime());
            newMaxTo.setDate(fromDate.getDate() + this.invoiceDownloadMaximumInterval);
            toMaxDate = new Date(Math.min(today.getTime(), newMaxTo.getTime()));
        }
        const downloadUrl = toDate
            ? `/api/v1/poolcorp/customers/invoices/download?beginDate=${fromDate.toISOString().split("T")[0]}&endDate=${
                  toDate.toISOString().split("T")[0]
              }`
            : "";

        const downloadHandler = () => {
            if (toDate) {
                downloadCsv(downloadUrl, "InvoiceHistory.csv");
                this.closeModal();
            }
        };

        const formFieldsGrid = (
            <GridContainer {...styles.container}>
                <GridItem {...styles.fromGridItem}>
                    <DatePicker
                        {...styles.fromDate}
                        label={translate("Date From")}
                        format={this.datePickerFormat}
                        selectedDay={fromDate}
                        onDayChange={this.fromDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: fromMinDate,
                            maxDate: today,
                            ...styles.fromDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
                <GridItem {...styles.toGridItem}>
                    <DatePicker
                        {...styles.toDate}
                        label={translate("Date To")}
                        format={this.datePickerFormat}
                        selectedDay={toDate}
                        onDayChange={this.toDateChangeHandler}
                        dateTimePickerProps={{
                            clearIcon: null,
                            minDate: fromDate,
                            maxDate: toMaxDate,
                            ...styles.toDate?.dateTimePickerProps,
                        }}
                    />
                </GridItem>
            </GridContainer>
        );

        return (
            <>
                <Hidden above="md">
                    <Modal
                        isOpen={this.props.isDownloadModalOpen}
                        handleClose={this.closeModal}
                        {...styles.downloadModal}
                    >
                        <StyledWrapper {...styles.modalTitleWrapper}>
                            <Typography {...styles.modalTitle} data-test-selector="productListFilterList">
                                {translate("Download")}
                            </Typography>
                        </StyledWrapper>
                        {formFieldsGrid}
                        <Typography {...styles.downloadInvoiceNote}>
                            {siteMessage("InvoiceDownload_Limited_Note")}
                        </Typography>
                        <StyledWrapper {...styles.buttonsWrapper}>
                            <Button disabled={!toDate} {...styles.modalDownloadButton} onClick={downloadHandler}>
                                {translate("Download")}
                            </Button>
                        </StyledWrapper>
                    </Modal>
                </Hidden>
                <Hidden below="lg">
                    <Accordion headingLevel={2}>
                        <AccordionSection title={translate("Download")}>
                            {formFieldsGrid}
                            <StoreFormActions>
                                <Button disabled={!toDate} {...styles.downloadButton} onClick={downloadHandler}>
                                    {translate("Download")}
                                </Button>
                                <Typography {...styles.downloadInvoiceNote}>
                                    {siteMessage("InvoiceDownload_Limited_Note")}
                                </Typography>
                            </StoreFormActions>
                        </AccordionSection>
                    </Accordion>
                </Hidden>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryDownload),
    definition: {
        group: "Invoice History",
        displayName: "Invoices Download",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
